import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button, InputText, Text } from "@pentabd/ui";

import {
  CancelButton,
  ErrorModal,
  InputTextRow,
  MultiFileRow,
  SelectController,
  SuccessModal,
} from "@components";

import useActionCreator from "@redux/hooks/useActionCreator";
import { VendorCreateType } from "@type/procurements/vendor";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@constants/routes";
import { mobileNumberFormat } from "@validations/HRM/employee/generalInfoValidation";
import {
  PROCUREMENT_VENDOR,
  procurementVendorValidations,
} from "@validations/Procurement/vendor/vendorValidations";

import HistoryTable from "./HistoryTable";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

const INVENTORY_TYPES = [
  {
    label: "ICT",
    value: "ICT",
  },
  {
    label: "NON_ICT",
    value: "NON_ICT",
  },
  {
    label: "BOTH",
    value: "BOTH",
  },
];

interface AddNewVendorsFormProps {
  id: string | undefined;
  data: VendorCreateType | null;
}

export default function AddNewVendorsForm({
  id,
  data,
}: AddNewVendorsFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const {
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteSuccess,
    deleteError,
  } = useCustomSelector((store) => store.procurements.vendorState);
  const { vendorCreateActionHandler, vendorUpdateActionHandler } =
    useActionCreator();

  const {
    control,
    register,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<VendorCreateType>({
    // resolver:yupResolver(procurementVendorValidations),
    values: revertMultipleDocumentData(data, ["documents"]) as VendorCreateType,
  });

  const createSuccessAction = () => {
    navigate(`${ROUTES.PROCUREMENT.VENDORS}`);
  };
  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const activeStatus = watch("isActive");

  const onSubmit: SubmitHandler<VendorCreateType> = (data) => {
    if (id) {
      vendorUpdateActionHandler(
        id as string,
        convertMultipleDocumentData(data, ["documents"]),
      );
    } else {
      vendorCreateActionHandler(
        convertMultipleDocumentData(data, ["documents"]),
      );
    }
  };

  useEffect(() => {
    switch (true) {
      case createSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case updateSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case deleteSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case !!createError: {
        setIsErrorModalOpen(true);
        break;
      }
      case !!updateError: {
        setIsErrorModalOpen(true);
        break;
      }
      case !!deleteError: {
        setIsErrorModalOpen(true);
        break;
      }
      default:
        break;
    }
  }, [
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteError,
    deleteSuccess,
  ]);

  return (
    <div>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
      {/* modal end */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="container p-10">
          <div className="d-flex flex-column gap-10 pb-10 border-bottom">
            {/* <InputTextRow
              label="SOFTWARE_INVENTORY.FORM.SOFTWARE_NAME"
              register={{
                ...register(PROCUREMENT_VENDOR.NAME),
              }}
              error={errors as any}
              required
            /> */}

            <div className="row">
              <div className="col-md-12 col-lg-3 d-flex">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_1.NAME")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputText
                  {...register("vendorName", {
                    required: "Name is required",
                  })}
                  placeholder={t(
                    "PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_1.PLACEHOLDER",
                  )}
                  type="text"
                  error={errors as any}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3 d-flex">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_2.NAME")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputText
                  {...register("address", {
                    required: "Address is required",
                  })}
                  placeholder={t(
                    "PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_2.PLACEHOLDER",
                  )}
                  type="text"
                  error={errors as any}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3 d-flex">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_3.NAME")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputText
                  {...register("tradeLicenceNo", {
                    required: "Trade License No. is required",
                  })}
                  placeholder={t(
                    "PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_3.PLACEHOLDER",
                  )}
                  type="text"
                  error={errors as any}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3 d-flex">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_4.NAME")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputText
                  {...register("contactNo", {
                    required: "Contact No. is required",
                    pattern: {
                      value: mobileNumberFormat,
                      message: "Please provide a valid Mobile No.",
                    },
                  })}
                  placeholder={t(
                    "PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_4.PLACEHOLDER",
                  )}
                  type="text"
                  error={errors as any}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3 d-flex">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_5.NAME")}
                </Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <InputText
                  {...register("vendorTypes")}
                  placeholder={t(
                    "PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_5.PLACEHOLDER",
                  )}
                  type="text"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-3 d-flex">
                <Text weight="semibold" color="title" size="sm" component="p">
                  {t("PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_12.NAME")}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-7">
                <SelectController
                  placeholder={t(
                    "PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_12.PLACEHOLDER",
                  )}
                  control={control}
                  name={"vendorNature"}
                  register={{
                    ...register("vendorNature", {
                      required: "Vendor nature is required",
                    }),
                  }}
                  options={INVENTORY_TYPES}
                  setValue={setValue}
                  error={errors as any}
                />
              </div>
            </div>

            <MultiFileRow
              label="PROCUREMENT_ADD_VENDORS.FORM.FIELDS.FIELD_6"
              name={"documents"}
              control={control}
              setValue={setValue}
              register={register}
              error={errors}
              disabled={id ? true : false}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />
          </div>
          <div className="col-md-12 col-lg-10 d-flex justify-content-end mt-10 gap-8">
            <CancelButton />

            <Button fill="fill" htmlType="submit" type="primary" size="xs">
              {t("PROCUREMENT_ADD_VENDORS.FORM.SAVE_BUTTON.BUTTON_TEXT")}
            </Button>
          </div>
        </div>
      </form>
      {id && <HistoryTable id={id} />}
    </div>
  );
}
